/* HB 2026-10-12, round 38 (Vic on his iPhone, the live site, 15:30 CPH: "too few products are loaded around my scroll.
 * I can easily scroll up or down and quickly have 1 or 2 rows of products waiting to load. This is the case going both
 * UP and DOWN"). The first picture of every product card in the collection and search grid is fetched well before she
 * gets to it, in both directions, without touching the cards themselves:
 *
 *   - THE WINDOW: every card within four screens of the viewport, above and below, has its first picture (the one on
 *     show: the dress view, or the model view when she has switched to it) held by a picture object of its own that
 *     is not in the page. The browser fetches exactly the address the card will show (the same srcset and sizes, so
 *     the same choice, never a second size) and keeps it in its memory cache. When hb-video.js gives a far card's
 *     picture back (it puts pictures away two screens behind her, round 28), it comes straight from memory. Within a
 *     screen and a half the picture is also decoded ahead, so it paints the moment the card is on screen.
 *   - THE NEXT AND THE PREVIOUS PAGE: the infinite scroll (paginated-list.js) hands every page it has fetched ahead to
 *     this script before it is in the grid. Once she has started scrolling and the end of the grid (or its top, after
 *     she landed on page=N) is within six screens, that page's sixteen first pictures are fetched, so they are in
 *     memory when the page is added three screens ahead.
 *   - Nothing here writes to the grid while she scrolls (no src, srcset or loading changes on the cards): Shopify's
 *     web pixel manager serializes every change in the page for a pixel that asks for it, and the fewer changes the
 *     less the phone's main thread has to do. The only writes are on pages not yet in the grid (prime()).
 *   - Not on Data Saver or 2G; not before the page's own load (four seconds at most), so the first screen and its
 *     LCP picture are never kept waiting. The deep slides (2 and up) and the colour dots are left to hb-video.js.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || !document.querySelector || window.HBPreload) return;

  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  var SLOW = !!(conn && (conn.saveData || /(^|-)2g$/.test(String(conn.effectiveType || ''))));
  var HAS_IO = 'IntersectionObserver' in window;

  var WINDOW_SCREENS = 4; // first pictures held this far above and below the viewport
  var DECODE_SCREENS = 1.5; // and decoded ahead this far
  var PAGE_SCREENS = 6; // a fetched-ahead page's first pictures once the grid's edge is this near
  var PAGE_HOLD_MS = 120000; // a page's pictures held at most this long if the page never arrives

  var CARD = '[ref="cards[]"]';

  var open = false; // the page's own load is done
  var moved = false; // she has scrolled at all
  var startY = window.scrollY || 0;

  /* ---------- a card's first picture ---------- */

  function modelView() {
    return document.documentElement.classList.contains('product-view--model');
  }

  // The picture on show in the card's first slide (works on a card in the page and on one parsed but not added yet).
  function firstOf(card) {
    var slide = card.querySelector('slideshow-slide');
    var scope = slide || card;
    var want = modelView() ? 'img.product-view-image--model' : 'img.product-view-image--dress';
    return scope.querySelector(want) || scope.querySelector('img');
  }

  // What the browser needs to choose the same file the card will show; hb-video.js may have put it away in data-hb-shed-*.
  function spec(img) {
    if (!img) return null;
    var srcset = img.getAttribute('srcset') || img.getAttribute('data-hb-shed-srcset') || '';
    var src = img.getAttribute('src') || img.getAttribute('data-hb-shed-src') || '';
    if (!src && !srcset) return null;
    // "auto" in sizes only applies to a lazy picture laid out in the page; the rest of the list is what an eager one uses.
    var sizes = (img.getAttribute('sizes') || '').replace(/^\s*auto\s*(,\s*)?/i, '');
    return { src: src, srcset: srcset, sizes: sizes, key: srcset + '|' + src + '|' + sizes };
  }

  function fetchPicture(sp, priority) {
    var im = new Image();
    im.decoding = 'async';
    try { im.fetchPriority = priority || 'auto'; } catch (e) { /* older browsers */ }
    if (sp.sizes) im.sizes = sp.sizes;
    if (sp.srcset) im.srcset = sp.srcset;
    if (sp.src) im.src = sp.src;
    return im;
  }

  /* ---------- pages not in the grid yet: priorities set before they land (free: no observer sees it) ---------- */

  // In the model view the model picture of the first slide is the one on show, but the theme renders the dress picture
  // eager and high and the model picture lazy; on a page that is not in the grid yet the two swap. Pages added above
  // her (which: 'previous') are seen from their last row up: only their last eight cards keep the high priority.
  function prime(cards, which) {
    if (!cards || !cards.length) return;
    var model = modelView();
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (!card || !card.querySelector) continue;
      var slide = card.querySelector('slideshow-slide');
      if (model && slide) {
        var m = slide.querySelector('img.product-view-image--model');
        var d = slide.querySelector('img.product-view-image--dress');
        if (m && d && !m.getAttribute('data-hb-primed')) {
          m.setAttribute('loading', 'eager');
          m.setAttribute('fetchpriority', 'high');
          m.setAttribute('sizes', (m.getAttribute('sizes') || '').replace(/^\s*auto\s*(,\s*)?/i, ''));
          d.setAttribute('loading', 'lazy');
          d.setAttribute('fetchpriority', 'low');
          m.setAttribute('data-hb-primed', '1');
        }
      }
      if (which === 'previous' && i < cards.length - 8) {
        var shown = firstOf(card);
        if (shown && shown.getAttribute('fetchpriority') === 'high') shown.setAttribute('fetchpriority', 'auto');
      }
    }
  }

  /* ---------- the window: cards within four screens either way ---------- */

  var near = new Set(); // cards within WINDOW_SCREENS
  var close = new Set(); // cards within DECODE_SCREENS
  var held = new Map(); // card -> { key, im, decoded }
  var nearIo = null;
  var closeIo = null;

  function release(card) {
    held.delete(card);
  }

  function hold(card) {
    var sp = spec(firstOf(card));
    if (!sp) return null;
    var h = held.get(card);
    if (h && h.key === sp.key) return h;
    // within a screen and a half: high; further out: low, so the next rows are never queued behind far ones
    h = { key: sp.key, im: fetchPicture(sp, close.has(card) ? 'high' : 'low'), decoded: false };
    held.set(card, h);
    return h;
  }

  function decodeAhead(h) {
    if (!h || h.decoded || typeof h.im.decode !== 'function') return;
    h.decoded = true;
    h.im.decode().catch(function () { h.decoded = false; });
  }

  var pumpQueued = false;
  function pump() {
    pumpQueued = false;
    if (!open || SLOW) return;
    var list = [];
    near.forEach(function (card) {
      if (!card.isConnected) {
        near.delete(card);
        close.delete(card);
        release(card);
        return;
      }
      list.push(card);
    });
    // nearest first (the distance the observer last reported), so the link serves the next row before the far ones
    list.sort(function (a, b) { return (a.__hbPlD || 0) - (b.__hbPlD || 0); });
    for (var i = 0; i < list.length; i++) {
      var h = hold(list[i]);
      if (h && close.has(list[i])) decodeAhead(h);
    }
    pages();
  }
  function schedule() {
    if (pumpQueued) return;
    pumpQueued = true;
    (window.requestAnimationFrame || setTimeout)(pump);
  }

  function watchCard(card) {
    if (card.__hbPl || !nearIo) return;
    card.__hbPl = true;
    nearIo.observe(card);
    closeIo.observe(card);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(CARD) && root.closest('[ref="grid"]')) watchCard(root);
    var found = root.querySelectorAll(CARD);
    for (var i = 0; i < found.length; i++) if (found[i].closest('[ref="grid"]')) watchCard(found[i]);
  }

  if (HAS_IO) {
    var m = WINDOW_SCREENS * 100 + '%';
    nearIo = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var card = entries[i].target;
        var r = entries[i].boundingClientRect;
        var vh = window.innerHeight || 1;
        card.__hbPlD = r.top > vh ? r.top - vh : r.bottom < 0 ? -r.bottom : 0;
        if (entries[i].isIntersecting) near.add(card);
        else {
          near.delete(card);
          release(card);
        }
      }
      schedule();
    }, { rootMargin: m + ' 0px ' + m + ' 0px', threshold: 0 });
    var d = DECODE_SCREENS * 100 + '%';
    closeIo = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) close.add(entries[i].target);
        else close.delete(entries[i].target);
      }
      schedule();
    }, { rootMargin: d + ' 0px ' + d + ' 0px', threshold: 0 });
  }

  /* ---------- pages fetched ahead by the infinite scroll ---------- */

  var offered = new Map(); // page number -> { which, specs, ims, at }

  // paginated-list.js: a page it has fetched and parsed ahead (cards not in the grid yet), 'next' or 'previous'.
  function offer(page, cards, which) {
    if (!cards || !cards.length) return;
    which = which === 'previous' ? 'previous' : 'next';
    prime(cards, which);
    var specs = [];
    for (var i = 0; i < cards.length; i++) {
      var sp = spec(firstOf(cards[i]));
      if (sp) specs.push(sp);
    }
    if (which === 'previous') specs.reverse(); // she reaches its last row first
    offered.set(page, { which: which, specs: specs, ims: null, at: Date.now() });
    schedule();
  }

  // The page is in the grid now: the window takes over.
  function inserted(page) {
    offered.delete(page);
    schedule();
  }

  function reset() {
    offered.clear();
    held.clear();
  }

  // How far the grid's bottom (next) or top (previous) is from the viewport, in screens.
  function edge(which) {
    var grid = document.querySelector('[ref="grid"]');
    if (!grid) return Infinity;
    var r = grid.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    return which === 'next' ? Math.max(0, (r.bottom - vh) / vh) : Math.max(0, -r.top / vh);
  }

  // The pages in the grid now, from the cards' data-page (first and last).
  function gridRange() {
    var cards = document.querySelectorAll('[ref="grid"] > ' + CARD);
    if (!cards.length) return null;
    return {
      first: Number(cards[0].getAttribute('data-page')) || 1,
      last: Number(cards[cards.length - 1].getAttribute('data-page')) || 1
    };
  }

  function pages() {
    if (!offered.size) return;
    var range = gridRange();
    if (!range) return;
    var now = Date.now();
    offered.forEach(function (o, page) {
      // in the grid already, or held too long for a page that never came
      if ((o.which === 'next' && page <= range.last) || (o.which === 'previous' && page >= range.first) || (o.ims && now - o.at > PAGE_HOLD_MS)) offered.delete(page);
    });
    // only the page right after the grid's last one, and the one right before its first: never two ahead
    var pick = { next: range.last + 1, previous: range.first - 1 };
    ['next', 'previous'].forEach(function (which) {
      var page = pick[which];
      var o = offered.get(page);
      if (!o || o.which !== which || o.ims) return;
      if (!moved) return; // she has not scrolled: nothing beyond the page she is looking at
      if (edge(which) > PAGE_SCREENS) return;
      o.ims = o.specs.map(function (sp) { return fetchPicture(sp, 'auto'); });
      o.at = now;
    });
  }

  /* ---------- wiring ---------- */

  var scrollQueued = false;
  window.addEventListener('scroll', function () {
    if (!moved && Math.abs((window.scrollY || 0) - startY) > 40) moved = true;
    if (scrollQueued || !offered.size) return;
    scrollQueued = true;
    setTimeout(function () { scrollQueued = false; schedule(); }, 150);
  }, { passive: true });

  function go() {
    if (open) return;
    open = true;
    schedule();
  }
  if (document.readyState === 'complete') setTimeout(go, 0);
  else {
    window.addEventListener('load', function () { setTimeout(go, 0); });
    setTimeout(go, 4000); // a load event held up by a slow third party
  }

  function start() {
    scan(document);
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) if (added[j].nodeType === 1) scan(added[j]);
        }
      }).observe(document.body || document.documentElement, { childList: true, subtree: true });
    }
    // the view switch (dress / model) changes which picture is on show
    var view = modelView();
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (modelView() === view) return;
        view = modelView();
        held.clear();
        schedule();
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
  }

  window.HBPreload = {
    version: '2026-10-12r38',
    offer: offer,
    inserted: inserted,
    prime: prime,
    reset: reset,
    // for a test in the console: what is held and whether each held picture chose the same file as its card
    state: function () {
      var same = 0, differ = 0, pending = 0;
      held.forEach(function (h, card) {
        var img = firstOf(card);
        if (!img || !h.im.complete || !img.complete || !img.currentSrc) { pending++; return; }
        if (h.im.currentSrc === img.currentSrc) same++; else differ++;
      });
      var pageList = [];
      offered.forEach(function (o, page) { pageList.push(page + ':' + o.which + (o.ims ? ':fetched' : '')); });
      return { open: open, moved: moved, near: near.size, close: close.size, held: held.size, same: same, differ: differ, pending: pending, pages: pageList, slow: SLOW };
    }
  };

  // pages paginated-list.js offered before this script ran
  var q = window.HBPreloadQueue;
  if (q && q.length) for (var k = 0; k < q.length; k++) offer(q[k][0], q[k][1], q[k][2]);
  window.HBPreloadQueue = [];

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
